import { useState, useMemo, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import LocalizedLink from '@/components/layout/LocalizedLink'
import Breadcrumb from '@/components/layout/Breadcrumb'
import SurahFilters, { type FilterType } from '@/components/quran/SurahFilters'
import SurahCard from '@/components/quran/SurahCard'
import ContinueReadingSection from '@/components/quran/ContinueReadingSection'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { quranApi, type Surah, type SurahDetail } from '@/services/quranApiService'
import { mockSurahs } from '@/services/mockDataService'
import { Book, Headphones } from 'lucide-react'
import { audioApi, type Reciter } from '@/services/audioApiService'
import Card from '@/components/ui/Card'
import AudioPlayer from '@/components/quran/AudioPlayer'

export default function QuranIndexPage() {
  const { t, i18n } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [surahs, setSurahs] = useState<Surah[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reciters, setReciters] = useState<Reciter[]>([])
  const [selectedReciterId, setSelectedReciterId] = useState<string | null>(null)
  const [isLoadingReciters, setIsLoadingReciters] = useState(false)
  const [selectedSurahForListening, setSelectedSurahForListening] = useState<number | null>(null)
  const [surahDetail, setSurahDetail] = useState<SurahDetail | null>(null)
  const [isLoadingSurahDetail, setIsLoadingSurahDetail] = useState(false)

  // Fetch surahs on mount
  useEffect(() => {
    const fetchSurahs = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await quranApi.getSurahs()
        setSurahs(data)
      } catch (err) {
        console.error('Failed to fetch surahs:', err)
        setError('فشل تحميل قائمة السور')
        // Fallback to mock data
        setSurahs(mockSurahs.map((s) => ({
          id: s.number,
          number: s.number,
          name: s.name,
          nameEn: s.nameEn,
          ayahs: s.ayahs,
          revelationPlace: s.revelationPlace,
          revelationOrder: s.revelationOrder,
          juz: s.juz,
        })))
      } finally {
        setIsLoading(false)
      }
    }
    fetchSurahs()
  }, [])

  // Fetch reciters on mount
  useEffect(() => {
    const fetchReciters = async () => {
      try {
        setIsLoadingReciters(true)
        const data = await audioApi.getReciters()
        setReciters(data)
        // Set default reciter (usually ID 1 - AbdulBaset)
        if (data.length > 0) {
          const defaultReciter = data.find((r) => r.id === '1') || data[0]
          setSelectedReciterId(defaultReciter.id)
          // Save to localStorage for persistence
          localStorage.setItem('selectedReciterId', defaultReciter.id)
        }
      } catch (err) {
        console.error('Failed to fetch reciters:', err)
        // Set default even if fetch fails
        setSelectedReciterId('1')
      } finally {
        setIsLoadingReciters(false)
      }
    }
    fetchReciters()

    // Load saved reciter from localStorage
    const savedReciterId = localStorage.getItem('selectedReciterId')
    if (savedReciterId) {
      setSelectedReciterId(savedReciterId)
    }
  }, [])

  // Get last read surah
  const lastReadSurahNumber = parseInt(localStorage.getItem('lastReadSurahNumber') || '1', 10)
  const lastReadSurah = surahs.find((s) => s.number === lastReadSurahNumber) || surahs[0]
  const lastReadIndex = lastReadSurah ? surahs.findIndex((s) => s.number === lastReadSurah.number) : -1
  const previousSurah = lastReadIndex > 0 ? surahs[lastReadIndex - 1] : null
  const nextSurah = lastReadIndex < surahs.length - 1 ? surahs[lastReadIndex + 1] : null

  // Filter surahs when search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      const fetchFilteredSurahs = async () => {
        try {
          setIsLoading(true)
          const data = await quranApi.getSurahs(searchQuery)
          setSurahs(data)
        } catch (err) {
          console.error('Failed to search surahs:', err)
        } finally {
          setIsLoading(false)
        }
      }
      fetchFilteredSurahs()
    }
  }, [searchQuery])

  const filteredSurahs = useMemo(() => {
    let result = [...surahs]

    switch (filterType) {
      case 'revelation':
        result.sort((a, b) => a.revelationOrder - b.revelationOrder)
        break
      case 'meccan':
        result = result.filter((s) => s.revelationPlace === 'Mecca')
        break
      case 'medinan':
        result = result.filter((s) => s.revelationPlace === 'Medina')
        break
      default:
        result.sort((a, b) => a.number - b.number)
    }

    return result
  }, [surahs, filterType])

  // Handle listen button click
  const handleListen = async (surahNumber: number) => {
    if (!selectedReciterId) {
      alert('يرجى اختيار قارئ أولاً')
      return
    }
    
    setSelectedSurahForListening(surahNumber)
    setIsLoadingSurahDetail(true)
    
    try {
      const lang = i18n.language.split('-')[0] || 'en'
      const detail = await quranApi.getSurah(surahNumber, lang)
      setSurahDetail(detail)
      
      // Scroll to audio player
      setTimeout(() => {
        const audioPlayerElement = document.getElementById('audio-player-section')
        if (audioPlayerElement) {
          audioPlayerElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    } catch (err) {
      console.error('Failed to fetch surah detail:', err)
      setError('فشل تحميل بيانات السورة')
    } finally {
      setIsLoadingSurahDetail(false)
    }
  }

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('quran.index', 'فهرس القرآن') },
  ]

  return (
    <>
      <Helmet>
        <title>{t('quran.index', 'فهرس القرآن')} - سكينة</title>
        <meta name="description" content={t('quran.indexDescription', 'تصفح فهرس القرآن الكريم')} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('quran.index', 'فهرس القرآن')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('quran.indexSubtitle', 'تصفح واستمع إلى جميع سور القرآن الكريم البالغ عددها 114 سورة')}
          </p>
        </div>

        {/* Quick Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <LocalizedLink
            to="/quran/read"
            className="flex items-center gap-3 p-6 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 rounded-lg hover:shadow-md transition-all group"
          >
            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm group-hover:scale-110 transition-transform">
              <Book className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('quran.read', 'قراءة القرآن')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                اقرأ القرآن الكريم مع الترجمة
              </p>
            </div>
          </LocalizedLink>

          <LocalizedLink
            to="/quran/listen"
            className="flex items-center gap-3 p-6 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-lg hover:shadow-md transition-all group"
          >
            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm group-hover:scale-110 transition-transform">
              <Headphones className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('quran.listen', 'الاستماع للقرآن')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                استمع لتلاوات القرآن الكريم
              </p>
            </div>
          </LocalizedLink>
        </div>

        {/* Reciter Selector - Before Continue Reading */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              اختر القارئ للاستماع:
            </label>
            <select
              value={selectedReciterId || ''}
              onChange={(e) => {
                const reciterId = e.target.value
                setSelectedReciterId(reciterId)
                localStorage.setItem('selectedReciterId', reciterId)
                // AudioPlayer will automatically reload with new reciter
              }}
              className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              disabled={isLoadingReciters || reciters.length === 0}
            >
              {isLoadingReciters && <option>جاري التحميل...</option>}
              {!isLoadingReciters && reciters.length === 0 && <option>لا توجد قراء متاحة</option>}
              {reciters.map((reciter) => (
                <option key={reciter.id} value={reciter.id}>
                  {reciter.nameArabic || reciter.name}
                </option>
              ))}
            </select>
            {selectedReciterId && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                سيتم استخدام هذا القارئ لجميع السور عند الضغط على زر الاستماع
              </span>
            )}
          </div>
        </Card>

        {/* Continue Reading Section */}
        {lastReadSurah && (
          <ContinueReadingSection
            currentSurah={lastReadSurah}
            previousSurah={previousSurah}
            nextSurah={nextSurah}
            selectedReciterId={selectedReciterId}
            onListen={handleListen}
          />
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Audio Player Section - Show before filters when a surah is selected */}
        {selectedSurahForListening && (
          <div id="audio-player-section" className="mb-6">
            <Card className="p-6">
              {isLoadingSurahDetail ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">جاري تحميل بيانات السورة...</span>
                </div>
              ) : surahDetail ? (
                <>
                  <div className="mb-4">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {surahDetail.name}
                    </h2>
                    {surahDetail.nameEn && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {surahDetail.nameEn}
                      </p>
                    )}
                  </div>
                  <AudioPlayer
                    surahNumber={selectedSurahForListening}
                    ayahs={surahDetail.ayahsList.map((a) => ({
                      number: a.number,
                      text: a.arabicText,
                    }))}
                    initialReciterId={selectedReciterId}
                  />
                </>
              ) : (
                <div className="text-center py-4 text-gray-600 dark:text-gray-400">
                  فشل تحميل بيانات السورة
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Filters */}
        <SurahFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filterType={filterType}
          onFilterChange={setFilterType}
        />

        {/* Surahs Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSurahs.map((surah) => (
              <SurahCard 
                key={surah.number} 
                surah={surah} 
                selectedReciterId={selectedReciterId}
                onListen={() => handleListen(surah.number)}
              />
            ))}
          </div>
        )}

        {filteredSurahs.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">
              {t('quran.noSurahsFound', 'لم يتم العثور على سور')}
            </p>
          </div>
        )}
      </div>
    </>
  )
}

